import { useState } from 'react';
import axios from 'axios';
import {
  Buildings, FilePdf, Plus, PencilSimple, Trash, CaretDown, CaretRight, ArrowUpRight, Files,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import EspaceTree from './EspaceNode';
import EspaceFormDialog from './EspaceFormDialog';
import BatimentFormDialog from './BatimentFormDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BatimentNode({ batiment, chantierId, lots, onChanged }) {
  const [expanded, setExpanded] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [addEspaceOpen, setAddEspaceOpen] = useState(false);

  const rootEspaces = (batiment.espaces || []).filter(e => !e.parent_espace_id);
  const plansPdf = batiment.plans || [];

  const remove = async () => {
    if (!window.confirm(`Supprimer le bâtiment « ${batiment.nom} » et tout son contenu ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/chantiers/${chantierId}/batiments/${batiment.id}?force=true`, { withCredentials: true });
      toast.success('Bâtiment supprimé');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const totalEspaces = (batiment.espaces || []).length;
  const totalPdfs = plansPdf.length;

  return (
    <div className="border border-[#E0E0DB] bg-white" data-testid={`batiment-node-${batiment.id}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-[#1E3A5F] text-white group">
        <button onClick={() => setExpanded(e => !e)} className="text-white/70 hover:text-white"
          data-testid={`batiment-toggle-${batiment.id}`}>
          {expanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
        </button>
        <Buildings size={16} weight="fill" />
        <span className="font-semibold flex-1">{batiment.nom}</span>
        {batiment.adresse && <span className="text-[10px] text-white/60 hidden sm:inline">{batiment.adresse}</span>}
        <span className="text-[10px] text-white/60">
          {totalEspaces} espace(s)
          {totalPdfs > 0 ? ` · ${totalPdfs} plan(s) PDF` : ''}
        </span>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          <button onClick={() => setAddEspaceOpen(true)} className="p-1 text-white/70 hover:text-white"
            title="Ajouter un espace" data-testid={`batiment-add-espace-${batiment.id}`}>
            <Plus size={14} />
          </button>
          <button onClick={() => setEditOpen(true)} className="p-1 text-white/70 hover:text-white"
            title="Modifier" data-testid={`batiment-edit-${batiment.id}`}>
            <PencilSimple size={14} />
          </button>
          <button onClick={remove} className="p-1 text-white/70 hover:text-white"
            title="Supprimer" data-testid={`batiment-delete-${batiment.id}`}>
            <Trash size={14} />
          </button>
        </div>
      </div>

      {expanded && (
        <>
          {plansPdf.length > 0 && (
            <div className="bg-[#FAFAF9] border-b border-[#E0E0DB] py-1.5 px-3">
              <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 flex items-center gap-1">
                <Files size={10} /> Plans PDF associés
              </p>
              <div className="flex flex-wrap gap-2">
                {plansPdf.map(p => {
                  let url = null;
                  if (p.pdf_stored_name) {
                    url = `${API_URL}/api/chantiers/${chantierId}/plans/${p.id}/pdf`;
                  } else if (p.image_url) {
                    url = p.image_url.startsWith('http') ? p.image_url : `${API_URL}${p.image_url}`;
                  }
                  return (
                    <a key={p.id} href={url || '#'} target="_blank" rel="noreferrer"
                      onClick={e => { if (!url) e.preventDefault(); }}
                      className="text-xs px-2 py-1 border border-[#E0E0DB] bg-white hover:border-[#C75B39] flex items-center gap-1.5"
                      data-testid={`batiment-pdf-${p.id}`}>
                      <FilePdf size={11} className="text-[#C75B39]" />
                      <span>{p.nom}</span>
                      <ArrowUpRight size={10} className="text-[#6B6B6B]" />
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {rootEspaces.length === 0 ? (
            <div className="py-6 text-center text-xs text-[#9A9A9A]" data-testid={`batiment-empty-${batiment.id}`}>
              Aucun espace défini.
              <button onClick={() => setAddEspaceOpen(true)} className="ml-2 text-[#C75B39] hover:underline">
                Ajouter un premier espace
              </button>
            </div>
          ) : (
            <EspaceTree
              rootEspaces={rootEspaces}
              allEspaces={batiment.espaces}
              batimentId={batiment.id}
              chantierId={chantierId}
              lots={lots}
              onChanged={onChanged}
            />
          )}
        </>
      )}

      {editOpen && (
        <BatimentFormDialog chantierId={chantierId} batiment={batiment}
          onSaved={onChanged} onClose={() => setEditOpen(false)} />
      )}
      {addEspaceOpen && (
        <EspaceFormDialog chantierId={chantierId} batimentId={batiment.id} lots={lots}
          onSaved={onChanged} onClose={() => setAddEspaceOpen(false)} />
      )}
    </div>
  );
}
